import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Row, Col, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import moment from 'moment';

import Tabela from '~/Components/TabelaDinamica/Tabela';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  getCorrecoesAtivasGeracao,
  getCotacao,
  getMoedas,
  gerarTabela,
} from '~/Services/Service';

const TabGerarTabela = ({ familia, onGerado }) => {
  const [correcoes, setCorrecoes] = useState([]);
  const [moedas, setMoedas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gerando, setGerando] = useState(false);

  const [data, setData] = useState(moment().format('YYYY-MM-DD'));
  const [moedaSelecionada, setMoedaSelecionada] = useState(null);
  const [cotacaoAtual, setCotacaoAtual] = useState(0);
  const [cotacaoAnterior, setCotacaoAnterior] = useState(0);
  const [indice, setIndice] = useState(0);
  const [jurosPorFora, setJurosPorFora] = useState(false);
  const [selecionadas, setSelecionadas] = useState({});
  const [observacoes, setObservacoes] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);

  const carregarCorrecoes = useCallback(async () => {
    if (!familia) return;
    setLoading(true);
    try {
      const res = await getCorrecoesAtivasGeracao(familia);
      const dados = res || [];
      setCorrecoes(dados);
      const sel = {};
      const obs = {};
      dados.forEach((c) => {
        sel[c.codigo] = true;
        obs[c.codigo] = c.obs || '';
      });
      setSelecionadas(sel);
      setObservacoes(obs);
    } catch {
      toast.error('Erro ao carregar correções ativas');
    } finally {
      setLoading(false);
    }
  }, [familia]);

  useEffect(() => {
    carregarCorrecoes();
  }, [carregarCorrecoes]);

  useEffect(() => {
    getMoedas()
      .then((res) => setMoedas(res || []))
      .catch(() => {});
  }, []);

  const opcoesMoeda = [...moedas]
    .sort((a, b) => a.codigo_ativo - b.codigo_ativo)
    .map((m) => ({
      value: m.codigo_ativo,
      label: `${m.codigo_ativo} - ${m.nome}`,
    }));

  const handleBuscarCotacao = async () => {
    if (!moedaSelecionada || !data) {
      toast.error('Selecione moeda e data');
      return;
    }
    try {
      const res = await getCotacao(moedaSelecionada.value, data);
      const d = res || {};
      setCotacaoAtual(d.cotacao_atual || 0);
      setCotacaoAnterior(d.cotacao_anterior || 0);
      setIndice(d.indice || 0);
    } catch {
      toast.error('Erro ao buscar cotação');
    }
  };

  const handleToggleSelecionada = (codigo) => {
    setSelecionadas((prev) => ({ ...prev, [codigo]: !prev[codigo] }));
  };

  const handleObsChange = (codigo, valor) => {
    setObservacoes((prev) => ({ ...prev, [codigo]: valor }));
  };

  const correcoesSelecionadas = correcoes.filter((c) => selecionadas[c.codigo]);

  const handleGerar = () => {
    if (correcoesSelecionadas.length === 0) {
      toast.error('Selecione pelo menos uma correção');
      return;
    }
    setShowConfirm(true);
  };

  const confirmarGeracao = async (confirmado) => {
    setShowConfirm(false);
    if (!confirmado) return;

    setGerando(true);
    try {
      const payload = {
        familia,
        data,
        moeda: (moedaSelecionada && moedaSelecionada.value) || 0,
        indice,
        juros_por_fora: jurosPorFora,
        correcoes: correcoesSelecionadas.map((c) => ({
          codigo: c.codigo,
          codigo_taxa: c.codigo_taxa,
          observacao: observacoes[c.codigo] || '',
        })),
      };

      const res = await gerarTabela(payload);
      const qtd = (res && res.tabelas_geradas) || 0;
      toast.success(`${qtd} tabela(s) gerada(s) com sucesso`);
      handleLimpar();
      if (onGerado) onGerado();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        (err.response && err.response.data) ||
        'Erro ao gerar tabela';
      toast.error(msg);
    } finally {
      setGerando(false);
    }
  };

  const handleLimpar = () => {
    setData(moment().format('YYYY-MM-DD'));
    setMoedaSelecionada(null);
    setCotacaoAtual(0);
    setCotacaoAnterior(0);
    setIndice(0);
    setJurosPorFora(false);
  };

  const colunas = [
    {
      title: '',
      key: 'codigo',
      width: '50px',
      render: (v) => (
        <Form.Check
          type="checkbox"
          checked={!!selecionadas[v]}
          onChange={() => handleToggleSelecionada(v)}
        />
      ),
    },
    { title: 'Código', key: 'codigo', width: '80px' },
    { title: 'Descrição', key: 'descricao' },
    { title: 'Moeda', key: 'moeda', width: '80px' },
    {
      title: 'Taxa',
      key: 'taxa',
      width: '80px',
      render: (v) => (v != null ? `${v}%` : ''),
    },
    {
      title: 'Modalidade',
      key: 'modalidade',
      width: '100px',
      render: (v) => (v === 'P' ? 'PRICE' : v === 'O' ? 'PRÓPRIO' : v),
    },
    {
      title: 'Observação',
      key: 'codigo',
      render: (v) => (
        <Form.Control
          size="sm"
          value={observacoes[v] || ''}
          onChange={(e) => handleObsChange(v, e.target.value)}
          maxLength={250}
        />
      ),
    },
  ];

  if (loading) return <Loading />;

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Data</Form.Label>
                <Form.Control
                  type="date"
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Moeda/Índice</Form.Label>
                <Select
                  value={moedaSelecionada}
                  onChange={setMoedaSelecionada}
                  options={opcoesMoeda}
                  placeholder="Selecione..."
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  isClearable
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleBuscarCotacao}
                className="mb-3"
              >
                Buscar Cotação
              </Button>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Cotação Atual</Form.Label>
                <Form.Control value={cotacaoAtual} disabled />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Cotação Anterior</Form.Label>
                <Form.Control value={cotacaoAnterior} disabled />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Índice (%)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.000001"
                  value={indice}
                  onChange={(e) => setIndice(parseFloat(e.target.value) || 0)}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Form.Check
                type="checkbox"
                label="Juros por fora"
                checked={jurosPorFora}
                onChange={(e) => setJurosPorFora(e.target.checked)}
                className="mb-3"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <h6>Correções Ativas</h6>
      {correcoes.length === 0 ? (
        <p className="text-muted">
          Nenhuma correção ativa encontrada para este empreendimento.
        </p>
      ) : (
        <Tabela colunas={colunas} dados={correcoes} />
      )}

      <div className="d-flex justify-content-end mt-3">
        <Button
          variant="outline-primary"
          className="mr-2"
          onClick={handleLimpar}
        >
          Limpar
        </Button>
        <Button
          variant="primary"
          onClick={handleGerar}
          disabled={gerando || correcoesSelecionadas.length === 0}
        >
          {gerando ? 'Gerando...' : 'Gerar Tabela'}
        </Button>
      </div>

      <ModalConfirmacao
        show={showConfirm}
        onClose={confirmarGeracao}
        title="Gerar Tabela"
        message={`Gerar tabela para ${correcoesSelecionadas.length} correção(ões)?`}
      />
    </>
  );
};

export default TabGerarTabela;
